// Acceso a datos de la herramienta de ordenamiento de oportunidades (fase 2 del diseño
// docs/decisiones/2026-09-25-diseno-oportunidad-entidad.md; RPC y reglas en
// supabase/migrations/20260925_oportunidades_fase2_sync_inversa.sql).
//
// Contrato de las tres funciones, igual que guardarFechaCuotaFactura: nunca tiran ni devuelven un
// vacío que parezca éxito — devuelven `{ data }` o `{ error: string }` (10.170 / 10.177).
import { client } from "../supabase";

const ESTATUS_ABIERTOS = ["50%", "75%", "90%"];

const mensajeDe = (error, contexto) =>
  typeof error?.message === "string" && error.message.length > 0
    ? error.message
    : `${contexto}: error sin mensaje (code ${error?.code ?? "desconocido"})`;

/**
 * Nombre y estatus de las oportunidades con esos ids (las de las filas cargadas en Detalle).
 * @param {Array<number|string>} ids
 * @returns {Promise<{ data?: Map<number, {id, nombre, estatus, eliminada}>, error?: string }>}
 */
export const getOportunidadesPorIds = async (ids) => {
  const unicos = [...new Set((ids || []).filter((id) => id != null).map(Number))];
  if (unicos.length === 0) return { data: new Map() };
  try {
    const { data, error } = await client
      .from("oportunidades")
      .select("id, nombre, estatus, eliminada")
      .in("id", unicos);
    if (error) return { error: mensajeDe(error, "getOportunidadesPorIds") };
    return { data: new Map((data || []).map((o) => [Number(o.id), o])) };
  } catch (err) {
    return { error: err?.message || "Error de red al leer oportunidades." };
  }
};

/**
 * Oportunidades abiertas (50/75/90, no eliminadas) de un cliente en un país, con la cantidad de
 * cotizaciones vigentes — sale de la vista v_oportunidades_pipeline (misma fuente que la
 * auditoría SQL, 10.1).
 * @returns {Promise<{ data?: Array<{oportunidad_id, nombre, estatus, n_cotizaciones}>, error?: string }>}
 */
export const getOportunidadesAbiertasDelCliente = async ({ clientId, pais }) => {
  if (clientId == null || !pais) return { error: "Falta el cliente o el país de la cotización." };
  try {
    const { data, error } = await client
      .from("v_oportunidades_pipeline")
      .select("oportunidad_id, nombre, estatus, n_cotizaciones")
      .eq("client_id", Number(clientId))
      .eq("pais", pais)
      .in("estatus", ESTATUS_ABIERTOS)
      .order("nombre", { ascending: true });
    if (error) return { error: mensajeDe(error, "getOportunidadesAbiertasDelCliente") };
    return { data: data || [] };
  } catch (err) {
    return { error: err?.message || "Error de red al leer las oportunidades del cliente." };
  }
};

/**
 * Asigna una cotización a otra oportunidad del mismo cliente, a una nueva (oportunidadId null) o
 * renombra (oportunidadId = la actual + nombre). Todo en la RPC atómica
 * asignar_cotizacion_a_oportunidad (10.239); acá solo se llama y se traduce el resultado.
 * @param {{ tipo: 'sale'|'contrato', id: number, oportunidadId: number|null, nombre: string|null }} args
 * @returns {Promise<{ data?: object, error?: string }>} data = estado final que devuelve la RPC.
 */
export const asignarCotizacionAOportunidad = async ({ tipo, id, oportunidadId, nombre }) => {
  if (tipo !== "sale" && tipo !== "contrato") return { error: `Tipo de cotización inválido: "${tipo}".` };
  if (id == null) return { error: "Falta la cotización." };
  try {
    const { data, error } = await client.rpc("asignar_cotizacion_a_oportunidad", {
      p_tipo: tipo,
      p_id: Number(id),
      p_oportunidad_id: oportunidadId == null ? null : Number(oportunidadId),
      p_nombre_nueva: nombre == null || String(nombre).trim() === "" ? null : String(nombre).trim(),
    });
    if (error) return { error: mensajeDe(error, "asignar_cotizacion_a_oportunidad") };
    if (!data || !data.oportunidad) return { error: "La RPC no devolvió el estado final de la oportunidad." };
    return { data };
  } catch (err) {
    return { error: err?.message || "Error de red al asignar la cotización." };
  }
};

export const ESTATUS_ABIERTOS_OPORTUNIDAD = ESTATUS_ABIERTOS;
